import type { ComponentProps } from 'react'

import MobileDropdownBottomSheet from './MobileDropdownBottomSheet'
import { pickDataAttributes } from './pickDataAttributes'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'

type DropdownListProps = ComponentProps<typeof DropdownList>

interface Props extends DropdownListProps {
  /**
   * Title for the mobile bottom sheet only. Use it when the desktop dropdown
   * has no header but the sheet reads better with one. Setting it also replaces
   * the desktop `headerExtension` on mobile, so the sheet header is whatever
   * this title says.
   */
  mobileTitle?: ComponentProps<typeof MobileDropdownBottomSheet>['title']
  /**
   * Subtitle for the mobile bottom sheet only. Requires `mobileTitle`.
   */
  mobileSubtitle?: ComponentProps<typeof MobileDropdownBottomSheet>['subtitle']
  /**
   * How the mobile bottom sheet pads its content.
   * @default list
   */
  mobileContentPadding?: ComponentProps<typeof MobileDropdownBottomSheet>['contentPadding']
  /**
   * Keep the floating dropdown on mobile. Only for anchored surfaces such as
   * typeaheads, where a sheet would cover the field being typed into.
   */
  disableMobileSheet?: boolean
}

/**
 * Drop-in replacement for `DropdownList` that renders a native feeling bottom
 * sheet on mobile and the floating dropdown from tablet up.
 */
function ResponsiveDropdownList(props: Props) {
  const {
    mobileTitle,
    mobileSubtitle,
    mobileContentPadding,
    disableMobileSheet,
    ...dropdownProps
  } = props
  const isMobileScreen = useScreenSizeOnly('mobile')

  if (isMobileScreen && !disableMobileSheet) {
    return <MobileDropdownBottomSheet
      {...pickDataAttributes(dropdownProps)}
      open={dropdownProps.open}
      title={mobileTitle ?? dropdownProps.title}
      subtitle={mobileSubtitle ?? dropdownProps.subtitle}
      headerExtension={mobileTitle ? undefined : dropdownProps.headerExtension}
      contentPadding={mobileContentPadding}
      primaryActionProps={dropdownProps.primaryActionProps}
      isPrimaryActionHidden={dropdownProps.isPrimaryActionHidden}
      secondaryActionProps={dropdownProps.secondaryActionProps}
      isSecondaryActionHidden={dropdownProps.isSecondaryActionHidden}
      footerActionsLayout={dropdownProps.footerActionsLayout === 'stacked' ? 'stacked' : undefined}
      onClose={dropdownProps.onClose}
    >
      {dropdownProps.children}
    </MobileDropdownBottomSheet>
  }

  return <DropdownList {...dropdownProps} />
}

export default ResponsiveDropdownList
